import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { EASE } from "@/components/Reveal";

const LINKS = [
  { to: "/projects", label: "Projects", testid: "nav-link-projects" },
  { to: "/studio", label: "Studio", testid: "nav-link-studio" },
  { to: "/press", label: "Press", testid: "nav-link-press" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  const darkHero = pathname === "/" || (pathname.startsWith("/projects/") && pathname.split("/").length > 2);
  const onDark = darkHero && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 w-full z-50 transition-[background-color,border-color,color] duration-500 ${
          scrolled ? "bg-paper/90 backdrop-blur-md border-b border-line text-ink" : "bg-paper/0 border-b border-transparent"
        } ${onDark ? "text-paper" : "text-ink"}`}
      >
        <div className="flex items-center justify-between px-6 md:px-12 py-5">
          <Link to="/" data-testid="nav-logo" className="group leading-none">
            <span className="block font-serif text-2xl md:text-3xl tracking-tight">Medium Plenty</span>
            <span className={`block text-[9px] uppercase tracking-[0.32em] mt-1 ${onDark ? "text-paper/70" : "text-smoke"}`}>Architecture + Interiors</span>
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className="editorial-link font-sans text-[13px] tracking-[0.12em] uppercase"
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/inquiries"
              data-testid="nav-link-inquiries"
              className="group editorial-link font-sans font-bold text-[13px] tracking-[0.12em] uppercase inline-flex items-center gap-1.5"
            >
              Inquiries
              <ArrowUpRight
                size={15}
                strokeWidth={2}
                className="text-clay transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </Link>
          </nav>

          <button
            data-testid="nav-menu-button"
            aria-label="Open menu"
            onClick={() => setOpen(true)}
            className="md:hidden p-2 -mr-2"
          >
            <Menu size={22} strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-overlay"
            className="fixed inset-0 z-[60] bg-ink text-paper flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
          >
            <div className="flex items-center justify-between px-6 py-5">
              <span className="font-serif text-xl">Medium Plenty</span>
              <button data-testid="nav-close-button" aria-label="Close menu" onClick={() => setOpen(false)} className="p-2 -mr-2">
                <X size={24} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {[...LINKS, { to: "/inquiries", label: "Inquiries", testid: "nav-link-inquiries-mobile" }].map((l, i) => (
                <span key={l.to} className="block overflow-hidden">
                  <motion.span
                    className="block"
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    exit={{ y: "110%" }}
                    transition={{ duration: 0.8, ease: EASE, delay: 0.08 + i * 0.07 }}
                  >
                    <NavLink to={l.to} data-testid={`mobile-${l.testid}`} className="font-serif text-5xl py-2 block">
                      {l.label}
                    </NavLink>
                  </motion.span>
                </span>
              ))}
            </nav>
            <div className="px-8 pb-10 text-[11px] uppercase tracking-[0.3em] text-paper/50">
              California — Projects Worldwide
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
